<template>
    <div class="container-fluid p-admin-interview-trans-profiles">
        <div class="card">
            <div class="card-header">英語化問診票一覧</div>

            <div class="card-body pt-2">
                <div class="d-block d-md-flex justify-content-between align-items-center mb-3">
                    <div class="d-block d-md-flex flex-wrap align-items-center">
                        <div class="mt-2 mr-0 mb-2 mr-md-2 mb-md-0">
                            <div class="c-freeword input-group input-group-sm">
                                <input
                                    v-model="search.freeword"
                                    @blur="onChangeSearchFreeword"
                                    type="text"
                                    class="form-control"
                                    placeholder="フリーワード"
                                    style="min-width: 100px;">
                                <div class="input-group-append">
                                    <div
                                        class="c-freeword__btn input-group-text"
                                        @click="onChangeSearchFreeword">
                                        検索
                                    </div>
                                </div>
                            </div>
                        </div>
                    </div>
                </div>

                <div
                    v-if="inProgress && interviewTransProfiles === null"
                    class="p-5 d-flex justify-content-center align-items-center">
                    <b-spinner variant="yokumiru-admin"/>
                </div>
                <div
                    v-show="interviewTransProfiles !== null"
                    class="row justify-content-center">
                    <div class="col-md-12">
                        <b-table
                            striped
                            hover
                            :items="interviewTransProfiles"
                            :fields="fields"
                            :sort-by.sync="$store.state.adminInterviewTransProfiles.sortBy"
                            :sort-desc.sync="$store.state.adminInterviewTransProfiles.sortDesc"
                            @sort-changed="fetchInterviewTransProfiles"
                            responsive
                            class="table-bordered m-table">
                            <template v-slot:cell(user_full_name_kana)="data">
                                <template v-if="data.item.user">
                                    <a href="#" @click="showModalUserProfile(data.item, $event)">
                                        {{ data.item.user.full_name }} <span class="small">({{data.item.user.age}})</span>
                                    </a>
                                    <div class="small">{{ data.item.user.full_name_kana }}</div>
                                </template>
                                <template v-else>
                                    <span class="text-muted">退会済の{{ $locale.patient }}</span>
                                </template>
                            </template>

                            <template v-slot:cell(medical_course_id)="data">
                                <div v-if="data.item.medical_course">{{ data.item.medical_course.name }}</div>
                                <template v-else>
                                    <span class="text-muted">削除済みの{{ $locale.consultation_host }}科</span>
                                </template>
                            </template>

                            <template v-slot:cell(created_at)="data">
                                <span class="u-palt">{{ helper.dateForHuman(data.item.created_at, 'Asia/Tokyo') }}</span>
                                <span
                                    class="small font-weight-bold u-palt"
                                    :class="helper.dayForHumanCSS(data.item.created_at, 'Asia/Tokyo')">
                                    {{ helper.dayForHuman(data.item.created_at, 'Asia/Tokyo', { bracket: true }) }}
                                </span>
                                <span>{{ helper.timeForHuman(data.item.created_at, 'Asia/Tokyo') }}</span>
                            </template>

                            <template v-slot:cell(links)="data">
                                <b-button
                                    type="button"
                                    variant="yokumiru-blue"
                                    class="btn-sm m-2"
                                    @click="onClickExportBtn(data.item)">
                                    エクスポート
                                </b-button>
                            </template>
                        </b-table>

                        <div class="d-flex flex-wrap justify-content-between align-items-center">
                            <b-pagination
                                :total-rows="$store.state.adminInterviewTransProfiles.total"
                                :per-page="$store.state.adminInterviewTransProfiles.per_page"
                                v-model="$store.state.adminInterviewTransProfiles.current_page"
                                @change="fetchInterviewTransProfiles">
                            </b-pagination>

                            <div class="d-flex flex-wrap align-items-center">
                                <div class="mr-3 mb-3">
                                    <span class="small">{{ dispPaginationDetail }}</span>
                                </div>
                            </div>
                        </div>
                    </div>
                </div>
            </div>
        </div>

        <admin-user-profile-modal :timezone="timezone"/>
    </div>
</template>

<script>
import { mapMutations } from 'vuex'
import constants from '../../constants'
import YokumiruHelper from '../../YokumiruHelper'

export default {
    name: 'AdminInterviewTransProfiles',

    data: function () {
        return {
            helper: YokumiruHelper,
            inProgress: false,
            search: {
                freeword: null,
            },
            timezone: 'Asia/Tokyo',
            interviewTransProfiles: null,
            fields: [
                {
                    'key': 'id',
                    'label': '#',
                    sortable: true,
                },
                {
                    'key': 'user_full_name_kana',
                    'label': `${this.$locale.patient}名`,
                    sortable: true,
                },
                {
                    'key': 'medical_course_id',
                    'label': `${this.$locale.consultation_host}科`,
                    sortable: true,
                },
                {
                    'key': 'interview_trans_answers_count',
                    'label': '回答数',
                    sortable: false,
                },
                {
                    'key': 'created_at',
                    'label': '作成日',
                    sortable: true,
                },
                {
                    'key': 'links',
                    'label': '',
                    'class': 'text-center',
                    sortable: false,
                },
            ],
        }
    },

    computed: {
        /**
         * ページング詳細表示
         * @returns {string}
         */
        dispPaginationDetail: function () {
            const state = this.$store.state.adminInterviewTransProfiles
            return state.total > 0 ? `全${state.total}件中 ${state.from}～${state.to}件表示` : ''
        },
    },

    created: function () {
        this.initQueryFromState()
        this.initSearchCondition()
        this.fetchInterviewTransProfiles()
    },

    methods: {
        ...mapMutations({
            setQuery: 'adminInterviewTransProfiles/setQuery',
            setQueries: 'adminInterviewTransProfiles/setQueries',
        }),

        onChangeSearchFreeword: async function () {
            await this.setQuery({ key: 'freeword', value: this.search.freeword })
            await this.fetchInterviewTransProfiles(1)
        },

        /**
         * 英語化問診票一覧取得
         *
         * @param {number|object} param
         */
        fetchInterviewTransProfiles: async function (param = null) {
            if (this.inProgress) return
            this.inProgress = true

            this.interviewTransProfiles = null

            if (Number.isInteger(param) && param > 0) {
                await this.setQuery({ key: 'current_page', value: param })
            }
            else if (param !== null && param.hasOwnProperty('sortBy')) {
                await this.setQuery({ key: 'sortBy', value: param.sortBy })
                await this.setQuery({ key: 'sortDesc', value: param.sortDesc })
            }

            const endpoint = constants.admin.interview_trans_profiles.endpoint.base

            await axios.get(`${endpoint}${this.buildQuery()}`)
                .then(({data}) => {
                    this.interviewTransProfiles = data.data
                    this.setQueries(data.meta)
                })

            this.inProgress = false
        },

        /**
         * 検索条件の初期化（localStorageから復元）
         */
        initQueryFromState: function () {
            if (localStorage.getItem('yokumiruWeb')) {
                const data = JSON.parse(localStorage.getItem('yokumiruWeb'))
                if (data.adminInterviewTransProfiles) {
                    this.setQueries(data.adminInterviewTransProfiles)
                }
            }
        },

        /**
         * 検索条件の初期化
         */
        initSearchCondition: function () {
            this.search.freeword = this.$store.state.adminInterviewTransProfiles.freeword
        },

        /**
         * QueryString生成
         *
         * @param {boolean} paginate
         * @return {string}
         */
        buildQuery: function (paginate = true) {
            let query = '?'

            if (paginate) {
                query += `page=${this.$store.state.adminInterviewTransProfiles.current_page}`
                query += `&sort=${this.$store.state.adminInterviewTransProfiles.sortBy}`
                query += `&desc=${this.$store.state.adminInterviewTransProfiles.sortDesc}`
            }
            else {
                query += `sort=${this.$store.state.adminInterviewTransProfiles.sortBy}`
                query += `&desc=${this.$store.state.adminInterviewTransProfiles.sortDesc}`
            }

            query += `&freeword=${this.$store.state.adminInterviewTransProfiles.freeword || ''}`

            return query
        },

        /**
         * 会員詳細モーダル表示
         * {object} interviewTransProfile
         * {object} e
         */
        showModalUserProfile: function (interviewTransProfile, e) {
            e.preventDefault()
            bus.$emit(
                'open-modal-admin-user-profile',
                interviewTransProfile.user_id,
            )
        },

        /**
         * エクスポートボタン押下時
         * {object} profile
         */
        onClickExportBtn: async function (profile) {
            if (this.inProgress) return
            this.inProgress = true

            const endpoint = constants.admin.interview_trans_profiles.endpoint.base
            const options = { responseType : 'blob' }

            await axios.get(`${endpoint}/${profile.id}/export?locale=en`, options)
                .then((response) => {
                    const blob = new Blob([response.data], {
                        type: response.data.type,
                    })
                    saveAs(blob, `YOKUMIRU_Medical_Consultation_#${profile.id}_${moment().format('YYYYMMDD')}.pdf`)
                })
                .catch(() => {
                    bus.$emit('alert-danger', 'システムエラーが発生しました。')
                })

            await axios.get(`${endpoint}/${profile.id}/export?locale=ja`, options)
                .then((response) => {
                    const blob = new Blob([response.data], {
                        type: response.data.type,
                    })
                    saveAs(blob, `YOKUMIRU_英語化問診票_#${profile.id}_${moment().format('YYYYMMDD')}.pdf`)
                })
                .catch(() => {
                    bus.$emit('alert-danger', 'システムエラーが発生しました。')
                })

            this.inProgress = false
        },
    },
}
</script>

<style lang="scss" scoped>
</style>

<style lang="scss">
.p-admin-interview-trans-profiles table {
    font-size: 0.75rem;
}
</style>
